import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { SocialShareDropdown } from "@/components/SocialShareDropdown";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Copy, TrendingUp, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export default function Affiliate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [affiliateData, setAffiliateData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAffiliateData();
  }, []);

  const loadAffiliateData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get or create affiliate tracking
      const { data: tracking, error: trackingError } = await supabase
        .from("affiliate_tracking")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (trackingError && trackingError.code === 'PGRST116') {
        // Generate new affiliate link
        const { data: generated } = await supabase.functions.invoke("generate-affiliate-link");
        if (generated) {
          loadAffiliateData();
        }
        return;
      }

      // Calculate available balance from earnings_ledger (same logic as Earnings.tsx)
      const { data: ledgerData } = await supabase
        .from("earnings_ledger")
        .select("*")
        .eq("user_id", user.id)
        .in("transaction_type", ['affiliate_commission', 'payout']);

      const totalEarned = ledgerData
        ?.filter(tx => tx.transaction_type === 'affiliate_commission')
        .reduce((sum, tx) => sum + parseFloat(tx.amount.toString()), 0) || 0;

      const totalPaidOut = ledgerData
        ?.filter(tx => tx.transaction_type === 'payout')
        .reduce((sum, tx) => sum + Math.abs(parseFloat(tx.amount.toString())), 0) || 0;

      // Get pending payouts
      const { data: pendingPayouts } = await supabase
        .from("payouts")
        .select("amount")
        .eq("user_id", user.id)
        .eq("payout_type", "bank")
        .in("status", ['pending', 'processing']);

      const pendingAmount = pendingPayouts?.reduce((sum, p) => 
        sum + parseFloat(p.amount.toString()), 0) || 0;

      // Calculate consistent available balance
      const calculatedAvailableBalance = totalEarned - totalPaidOut - pendingAmount;

      setAffiliateData({
        ...tracking,
        calculated_total_earned: totalEarned,
        calculated_available_balance: calculatedAvailableBalance
      });
    } catch (error) {
      console.error("Error loading affiliate data:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = (link: string) => {
    navigator.clipboard.writeText(link);
    toast({ title: t("affiliate.linkCopied"), variant: "success" });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-surface">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <p className="font-heading text-xl">{t("common.loading")}...</p>
        </main>
        <Footer />
      </div>
    );
  }

  const affiliateLink = affiliateData?.custom_url || "";
  const stats = {
    monthlyClicks: affiliateData?.total_clicks || 0,
    clicksGrowth: 0,
    totalConversions: affiliateData?.total_conversions || 0,
    conversionRate: affiliateData?.total_clicks > 0 
      ? ((affiliateData?.total_conversions / affiliateData?.total_clicks) * 100).toFixed(1)
      : 0,
    totalEarnings: affiliateData?.calculated_total_earned ?? parseFloat(affiliateData?.total_earned || 0),
    availableBalance: affiliateData?.calculated_available_balance ?? (parseFloat(affiliateData?.total_earned || 0) - parseFloat(affiliateData?.pending_payout || 0))
  };

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8 max-w-7xl">
        {/* Breadcrumb Navigation */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/dashboard">
                {t("common.dashboard")}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("affiliate.title")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Back Button + Title */}
        <div className="flex items-center gap-4 mb-2">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("common.back")}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          
          <h1 className="font-heading text-3xl font-bold text-dark">
            {t("affiliate.title")}
          </h1>
        </div>
        <p className="text-muted-foreground mb-8 ml-14">
          {t("affiliate.description")}
        </p>

        {/* Main Affiliate Link */}
        <BrutalCard className="p-6">
          <h2 className="font-heading text-xl mb-4">{t("affiliate.yourLink")}</h2>

          <div className="flex flex-col sm:flex-row gap-3">
            <BrutalInput
              value={affiliateLink}
              readOnly
              className="w-full"
            />
            <BrutalButton onClick={() => copyLink(affiliateLink)} className="w-full sm:w-auto">
              <Copy className="w-4 h-4 mr-2" />
              {t("affiliate.copy")}
            </BrutalButton>
            <SocialShareDropdown
              url={affiliateLink}
              title={t("affiliate.shareTitle", "Unisciti a noi!")}
              description={t("affiliate.shareDescription", "Scopri questa fantastica piattaforma")}
              buttonText={t("common.share", "Condividi")}
              className="w-full sm:w-auto"
            />
          </div>
        </BrutalCard>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8 mb-8">
        <BrutalCard className="p-6">
          <div className="text-sm text-muted-foreground">{t("affiliate.monthlyClicks")}</div>
          <div className="text-3xl font-bold">{stats.monthlyClicks}</div>
          {stats.clicksGrowth > 0 && (
            <div className="text-xs text-success flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              +{stats.clicksGrowth}% {t("affiliate.vsLastMonth")}
            </div>
          )}
        </BrutalCard>

        <BrutalCard className="p-6">
          <div className="text-sm text-muted-foreground">{t("affiliate.conversions")}</div>
          <div className="text-3xl font-bold">{stats.totalConversions}</div>
          <div className="text-xs text-muted-foreground">
            {t("affiliate.rate")}: {stats.conversionRate}%
          </div>
        </BrutalCard>

        <BrutalCard className="p-6">
          <div className="text-sm text-muted-foreground">{t("affiliate.totalEarnings")}</div>
          <div className="text-3xl font-bold">€{stats.totalEarnings.toFixed(2)}</div>
          <div className="text-xs text-muted-foreground">€10 {t("affiliate.perConversion")}</div>
        </BrutalCard>

        <BrutalCard className="p-6">
          <div className="text-sm text-muted-foreground">{t("affiliate.availableBalance")}</div>
          <div className="text-3xl font-bold">€{stats.availableBalance.toFixed(2)}</div>
          <BrutalButton
            size="sm"
            className="mt-2 w-full"
            disabled={stats.availableBalance < 50}
            onClick={() => window.location.href = '/dashboard/earnings'}
          >
            {t("affiliate.requestPayout")}
          </BrutalButton>
        </BrutalCard>
        </div>

        {/* FAQ / Terms and Conditions */}
        <BrutalCard className="p-6 mt-8">
          <h2 className="font-heading text-xl mb-4">{t("affiliate.faq.title")}</h2>
          
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="how-it-works">
              <AccordionTrigger className="text-left font-medium">
                {t("affiliate.faq.q1")}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                <div dangerouslySetInnerHTML={{ __html: t("affiliate.faq.a1") }} />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="active-customer">
              <AccordionTrigger className="text-left font-medium">
                {t("affiliate.faq.q2")}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                <div dangerouslySetInnerHTML={{ __html: t("affiliate.faq.a2") }} />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="rewards">
              <AccordionTrigger className="text-left font-medium">
                {t("affiliate.faq.q3")}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                <div dangerouslySetInnerHTML={{ __html: t("affiliate.faq.a3") }} />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="payout">
              <AccordionTrigger className="text-left font-medium">
                {t("affiliate.faq.q4")}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                <div dangerouslySetInnerHTML={{ __html: t("affiliate.faq.a4") }} />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="link-expiry">
              <AccordionTrigger className="text-left font-medium">
                {t("affiliate.faq.q5")}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                <div dangerouslySetInnerHTML={{ __html: t("affiliate.faq.a5") }} />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="ideas">
              <AccordionTrigger className="text-left font-medium">
                {t("affiliate.faq.q6")}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                <div dangerouslySetInnerHTML={{ __html: t("affiliate.faq.a6") }} />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="friend-discount">
              <AccordionTrigger className="text-left font-medium">
                {t("affiliate.faq.q7")}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                <div dangerouslySetInnerHTML={{ __html: t("affiliate.faq.a7") }} />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </BrutalCard>
      </main>
      
      <Footer />
    </div>
  );
}
